import axios from "axios";
import { hostname } from "../../utils";

/**
 * Fetch companies accessible to the current user.
 */
export async function fetchCompanies(getToken: Function) {
  const tok = await getToken();
  const res = await axios.get(`${hostname}/companies`, {
    headers: { Authorization: `Bearer ${tok}` },
  });
  return res.data.companies;
}

/**
 * Fetch integrations for a given company.
 */
export async function fetchIntegrations(
  companyId: string,
  getToken: Function,
  page?: number,
  pageSize?: number,
  search?: string
) {
  const tok = await getToken();
  const params: any = { companyId };

  if (page !== undefined) {
    params.page = page;
  }
  if (pageSize !== undefined) {
    params.pageSize = pageSize;
  }
  if (search !== undefined && search.trim() !== '') {
    params.search = search.trim();
  }

  const res = await axios.get(`${hostname}/integrations`, {
    params,
    headers: { Authorization: `Bearer ${tok}` },
  });
  return res.data;
}

/**
 * Fetch integrations by type (returns minimal data for table)
 */
export async function fetchIntegrationsByType(
  companyId: string,
  integrationType: string,
  getToken: Function
) {
  const tok = await getToken();
  const res = await axios.get(
    `${hostname}/integrations/by-type/${integrationType}`,
    {
      params: { companyId },
      headers: { Authorization: `Bearer ${tok}` },
    }
  );
  return res.data.items;
}

/**
 * Fetch full integration details by ID for editing
 */
export async function fetchIntegrationById(
  integrationId: string,
  companyId: string,
  getToken: Function
) {
  const tok = await getToken();
  const res = await axios.get(`${hostname}/integrations/${integrationId}`, {
    params: { companyId },
    headers: { Authorization: `Bearer ${tok}` },
  });
  return res.data.integration;
}

/**
 * Remove an integration by ID.
 */
export async function removeIntegration(
  integrationId: string,
  companyId: string,
  getToken: Function
) {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/remove`,
    { integrationId, companyId },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Trigger sync on an integration.
 */
export async function syncIntegration(
  instanceName: string,
  type: string,
  companyId: string,
  getToken: Function
) {
  const tok = await getToken();
  await axios.post(
    `${hostname}/integrations/${type}`,
    { companyId, instanceName },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
}

/**
 * Update an integration instance.
 */
export async function updateIntegration(
  companyId: string,
  integration: any,
  getToken: Function
) {
  const tok = await getToken();
  const response = await axios.post(
    `${hostname}/integrations/update`,
    {
      companyId,
      integration,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return response.data;
}

/**
 * Fetch available Composio apps catalog
 */
export async function fetchComposioApps(
  getToken: () => Promise<string>,
  category?: string,
  search?: string
) {
  const tok = await getToken();
  const params: any = {};
  if (category) params.category = category;
  if (search) params.search = search;

  const res = await axios.get(`${hostname}/integrations/composio/apps`, {
    params,
    headers: { Authorization: `Bearer ${tok}` },
  });
  return res.data;
}

/**
 * Start Composio OAuth connection for a generic app
 */
export async function startComposioConnect(
  companyId: string,
  appSlug: string,
  instanceName: string,
  getToken: () => Promise<string>
) {
  const tok = await getToken();
  const res = await axios.post(
    `${hostname}/integrations/composio/connect`,
    {
      companyId,
      integrationType: appSlug,
      instanceName,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data;
}

/**
 * Check Composio connection status
 */
export async function checkComposioStatus(
  companyId: string,
  appSlug: string,
  getToken: () => Promise<string>
) {
  const tok = await getToken();
  const res = await axios.get(`${hostname}/integrations/composio/status`, {
    params: { companyId, integrationType: appSlug },
    headers: { Authorization: `Bearer ${tok}` },
  });
  return res.data;
}

/**
 * Install a generic Composio integration
 */
export async function installGenericComposioIntegration(
  companyId: string,
  instanceName: string,
  appSlug: string,
  composioSlug: string,
  composioConnectedAccountId: string,
  logo: string,
  description: string,
  getToken: () => Promise<string>
) {
  const tok = await getToken();
  const res = await axios.post(
    `${hostname}/integrations/composio/install-generic`,
    {
      companyId,
      instanceName,
      appSlug,
      composioSlug,
      composioConnectedAccountId,
      logo,
      description,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data;
}

/**
 * Get required auth fields for an API-key based integration
 */
export async function getComposioAuthFields(
  appSlug: string,
  getToken: () => Promise<string>
) {
  const tok = await getToken();
  const res = await axios.get(
    `${hostname}/integrations/composio/auth-fields/${appSlug}`,
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data;
}

/**
 * Connect a Composio integration with API key or credentials
 */
export async function connectComposioWithApiKey(
  companyId: string,
  instanceName: string,
  appSlug: string,
  apiKey: string,
  logo: string,
  description: string,
  getToken: () => Promise<string>,
  credentials?: Record<string, string>
) {
  const tok = await getToken();
  const res = await axios.post(
    `${hostname}/integrations/composio/connect-apikey`,
    {
      companyId,
      instanceName,
      appSlug,
      apiKey,
      logo,
      description,
      credentials,
    },
    { headers: { Authorization: `Bearer ${tok}` } }
  );
  return res.data;
}
